import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CaretDownIcon,
  WarningCircleIcon,
  SparkleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import { RebateTierProximity, ThresholdType } from '@erp/shared-types';
import { Progress } from '@/components/ui/progress';
import { useNavigate } from '@tanstack/react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import SupplierPill from '@/components/company/SupplierPill';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface GroupedAgreement {
  agreementId: string;
  agreementName: string;
  supplierId: string;
  supplierName: string;
  supplierTags?: Array<{ id: string; name: string }>;
  rules: RebateTierProximity[];
  closestVolumeNeeded: number | null;
  highestDelta: number | null;
  highestProgress: number | null;
  bestValue: number | null; // net benefit: rebateDelta - volumeNeeded
  bestRatio: number | null; // efficiency: rebateDelta / volumeNeeded
  hasErrors: boolean;
  hasHighValueRule: boolean; // true if any rule has rebateDelta > volumeNeeded
}

interface TierProximityGroupedViewProps {
  data: RebateTierProximity[];
  filters: {
    supplierFilter: string[];
    tagFilter: string[];
    thresholdTypeFilter: string[];
    retroactiveFilter: boolean[];
    includeMissingBaseline: boolean;
    sortBy: 'closest' | 'highestDelta' | 'progress' | 'bestValue' | 'bestRatio';
    visibleColumns: string[];
  };
}

export function TierProximityGroupedView({
  data,
  filters,
}: TierProximityGroupedViewProps) {
  const navigate = useNavigate();
  const [expandedAgreements, setExpandedAgreements] = useState<Set<string>>(
    new Set(),
  );

  const formatCurrency = (value: number | null): string => {
    if (value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Group data by agreement and apply filters
  const groupedData = useMemo(() => {
    // Filter by supplier
    let filtered = data;
    if (filters.supplierFilter.length > 0) {
      filtered = filtered.filter((item) =>
        filters.supplierFilter.includes(item.supplierId),
      );
    }

    // Filter by tags
    if (filters.tagFilter.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.supplierTags || item.supplierTags.length === 0) {
          return false;
        }
        const itemTagIds = item.supplierTags.map((tag) => tag.id);
        return filters.tagFilter.some((tagId) => itemTagIds.includes(tagId));
      });
    }

    // Group by agreement
    const grouped = new Map<string, GroupedAgreement>();

    for (const item of filtered) {
      const key = item.agreementId;
      if (!grouped.has(key)) {
        grouped.set(key, {
          agreementId: item.agreementId,
          agreementName: item.agreementName,
          supplierId: item.supplierId,
          supplierName: item.supplierName,
          supplierTags: item.supplierTags,
          rules: [],
          closestVolumeNeeded: null,
          highestDelta: null,
          highestProgress: null,
          bestValue: null,
          bestRatio: null,
          hasErrors: false,
          hasHighValueRule: false,
        });
      }

      const group = grouped.get(key)!;
      group.rules.push(item);

      // Track closest volume needed (smallest non-null value)
      if (item.volumeNeeded !== null) {
        if (
          group.closestVolumeNeeded === null ||
          item.volumeNeeded < group.closestVolumeNeeded
        ) {
          group.closestVolumeNeeded = item.volumeNeeded;
        }
      }

      // Track highest rebate delta (largest non-null value)
      if (item.rebateDelta !== null) {
        if (
          group.highestDelta === null ||
          item.rebateDelta > group.highestDelta
        ) {
          group.highestDelta = item.rebateDelta;
        }
      }

      // Track highest progress percentage (largest non-null value)
      if (item.volumeNeeded !== null) {
        // Only track progress if there's a next tier (volumeNeeded is not null)
        if (
          group.highestProgress === null ||
          item.percentageComplete > group.highestProgress
        ) {
          group.highestProgress = item.percentageComplete;
        }
      }

      // Track best value (net benefit) and best ratio (efficiency)
      // Only calculate if both rebateDelta and volumeNeeded are non-null and no error
      if (
        !item.error &&
        item.rebateDelta !== null &&
        item.volumeNeeded !== null
      ) {
        const netBenefit = item.rebateDelta - item.volumeNeeded;
        const efficiencyRatio = item.rebateDelta / item.volumeNeeded;

        // Track best net benefit (highest value)
        if (group.bestValue === null || netBenefit > group.bestValue) {
          group.bestValue = netBenefit;
        }

        // Track best efficiency ratio (highest value)
        if (group.bestRatio === null || efficiencyRatio > group.bestRatio) {
          group.bestRatio = efficiencyRatio;
        }

        // Check if this is a high-value rule (rebateDelta > volumeNeeded)
        if (item.rebateDelta > item.volumeNeeded) {
          group.hasHighValueRule = true;
        }
      }

      if (item.error) {
        group.hasErrors = true;
      }
    }

    // Sort rules within each group by volume needed
    for (const group of grouped.values()) {
      group.rules.sort((a, b) => {
        // Errors go to end
        if (a.error && b.error) return 0;
        if (a.error) return 1;
        if (b.error) return -1;

        // Null values (at highest tier) go after valid entries
        if (a.volumeNeeded === null && b.volumeNeeded === null) return 0;
        if (a.volumeNeeded === null) return 1;
        if (b.volumeNeeded === null) return -1;

        // Sort by volume needed (ascending)
        return a.volumeNeeded - b.volumeNeeded;
      });
    }

    // Convert to array and sort groups based on selected sort option
    const sorted = Array.from(grouped.values()).sort((a, b) => {
      // Groups with errors go to end
      if (a.hasErrors && b.hasErrors) return 0;
      if (a.hasErrors) return 1;
      if (b.hasErrors) return -1;

      if (filters.sortBy === 'closest') {
        // Sort by closest volume needed (ascending)
        if (a.closestVolumeNeeded === null && b.closestVolumeNeeded === null)
          return 0;
        if (a.closestVolumeNeeded === null) return 1;
        if (b.closestVolumeNeeded === null) return -1;
        return a.closestVolumeNeeded - b.closestVolumeNeeded;
      } else if (filters.sortBy === 'highestDelta') {
        // Sort by highest rebate delta (descending)
        if (a.highestDelta === null && b.highestDelta === null) return 0;
        if (a.highestDelta === null) return 1;
        if (b.highestDelta === null) return -1;
        return b.highestDelta - a.highestDelta;
      } else if (filters.sortBy === 'progress') {
        // Sort by highest progress (descending)
        if (a.highestProgress === null && b.highestProgress === null) return 0;
        if (a.highestProgress === null) return 1;
        if (b.highestProgress === null) return -1;
        return b.highestProgress - a.highestProgress;
      } else if (filters.sortBy === 'bestValue') {
        // Sort by best net benefit (descending), nulls at end
        if (a.bestValue === null && b.bestValue === null) return 0;
        if (a.bestValue === null) return 1;
        if (b.bestValue === null) return -1;
        return b.bestValue - a.bestValue;
      } else {
        // Sort by best efficiency ratio (descending), nulls at end
        if (a.bestRatio === null && b.bestRatio === null) return 0;
        if (a.bestRatio === null) return 1;
        if (b.bestRatio === null) return -1;
        return b.bestRatio - a.bestRatio;
      }
    });

    return sorted;
  }, [data, filters]);

  // Initialize all agreements as expanded by default
  useEffect(() => {
    const allAgreementIds = new Set(groupedData.map((g) => g.agreementId));
    // Only update if we have new agreements that aren't already expanded
    setExpandedAgreements((prev) => {
      const updated = new Set(prev);
      allAgreementIds.forEach((id) => updated.add(id));
      return updated;
    });
  }, [groupedData]);

  const toggleAgreement = (agreementId: string) => {
    const newExpanded = new Set(expandedAgreements);
    if (newExpanded.has(agreementId)) {
      newExpanded.delete(agreementId);
    } else {
      newExpanded.add(agreementId);
    }
    setExpandedAgreements(newExpanded);
  };

  if (groupedData.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No tier proximity data available with current filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedData.map((group) => {
        const isExpanded = expandedAgreements.has(group.agreementId);

        return (
          <Card
            key={group.agreementId}
            className={`overflow-hidden ${
              group.hasHighValueRule ? 'border-l-4 border-yellow-500' : ''
            }`}
          >
            <Collapsible
              open={isExpanded}
              onOpenChange={() => toggleAgreement(group.agreementId)}
            >
              <CollapsibleTrigger asChild>
                <CardHeader
                  className={`cursor-pointer hover:bg-accent/50 transition-colors ${
                    group.hasHighValueRule ? 'bg-yellow-50/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Title line with index + agreement name + supplier + rule count */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {/* 1) numbering */}
                        <span className="font-semibold text-lg text-muted-foreground">
                          {groupedData.indexOf(group) + 1}.
                        </span>

                        {/* Agreement name (clickable) */}
                        <CardTitle className="text-lg">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate({
                                to: '/rebates/agreements/$id',
                                params: { id: group.agreementId },
                              });
                            }}
                            className="text-primary hover:underline"
                          >
                            {group.agreementName}
                          </button>
                        </CardTitle>

                        {/* Supplier pill */}
                        <SupplierPill
                          supplierId={group.supplierId}
                          supplier={{
                            name: group.supplierName,
                            id: group.supplierId,
                          }}
                        />

                        {/* Supplier tags (unchanged) */}
                        {group.supplierTags &&
                          group.supplierTags.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              {group.supplierTags.map((tag) => (
                                <Badge key={tag.id} variant="secondary">
                                  {tag.name}
                                </Badge>
                              ))}
                            </div>
                          )}

                        {/* NEW: Rule count */}
                        <span className="text-sm text-muted-foreground">
                          {group.rules.length} applicable{' '}
                          {group.rules.length === 1 ? 'rule' : 'rules'}
                        </span>

                        {/* High value badge */}
                        {group.hasHighValueRule && (
                          <Badge
                            variant="outline"
                            className="border-yellow-500 text-yellow-700 bg-yellow-50"
                          >
                            <SparkleIcon className="h-3 w-3 mr-1" />
                            High Value
                          </Badge>
                        )}

                        {/* Error badge (unchanged) */}
                        {group.hasErrors && (
                          <Badge variant="destructive">
                            <WarningCircleIcon className="h-3 w-3 mr-1" />
                            Errors
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CaretDownIcon
                      className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <TooltipProvider>
                      <Table className="table-fixed w-full min-w-[1620px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[200px]">Rule</TableHead>
                            {filters.visibleColumns.includes(
                              'productGroups',
                            ) && (
                              <TableHead className="w-[250px]">
                                Product Groups
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes('currentTier') && (
                              <TableHead className="w-[150px]">
                                Current Tier
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes('nextTier') && (
                              <TableHead className="w-[150px]">
                                Next Tier
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'currentVolume',
                            ) && (
                              <TableHead className="w-[140px]">
                                Current Volume
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'nextThreshold',
                            ) && (
                              <TableHead className="w-[140px]">
                                Next Threshold
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'volumeNeeded',
                            ) && (
                              <TableHead className="w-[140px]">
                                Volume Needed
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'currentRebate',
                            ) && (
                              <TableHead className="w-[140px]">
                                Current Rebate
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'nextTierRebate',
                            ) && (
                              <TableHead className="w-[140px]">
                                Next Tier Rebate
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes('rebateDelta') && (
                              <TableHead className="w-[140px]">
                                Rebate Delta
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes('netBenefit') && (
                              <TableHead className="w-[140px]">
                                <div className="flex items-center gap-1.5">
                                  <span>Net Benefit</span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        Calculated as Rebate Delta minus Volume
                                        Needed. Shows the net benefit of
                                        reaching the next tier (positive means
                                        rebate increase exceeds volume
                                        investment needed).
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes(
                              'efficiencyRatio',
                            ) && (
                              <TableHead className="w-[140px]">
                                <div className="flex items-center gap-1.5">
                                  <span>Efficiency Ratio</span>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>
                                        Calculated as Rebate Delta divided by
                                        Volume Needed. Shows how much rebate
                                        increase you get per dollar of volume
                                        needed to reach the next tier.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableHead>
                            )}
                            {filters.visibleColumns.includes('progress') && (
                              <TableHead className="w-[250px]">
                                Progress
                              </TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.rules.map((rule) => {
                            const isHighValueRule =
                              !rule.error &&
                              rule.rebateDelta !== null &&
                              rule.volumeNeeded !== null &&
                              rule.rebateDelta > rule.volumeNeeded;

                            return (
                              <TableRow
                                key={rule.ruleId}
                                className={
                                  isHighValueRule
                                    ? 'bg-yellow-50/50 border-l-4 border-yellow-500'
                                    : ''
                                }
                              >
                                <TableCell className="font-medium w-[200px]">
                                  <div className="flex flex-col gap-1">
                                    <span>{rule.ruleName}</span>
                                    <div className="flex flex-wrap gap-1">
                                      {rule.thresholdType && (
                                        <Badge
                                          variant={
                                            rule.thresholdType ===
                                            ThresholdType.GROWTH
                                              ? 'default'
                                              : 'secondary'
                                          }
                                          className="text-xs"
                                        >
                                          {rule.thresholdType ===
                                          ThresholdType.GROWTH
                                            ? 'Growth'
                                            : 'Volume'}
                                        </Badge>
                                      )}
                                      {rule.isRetroactiveToDollarOne ===
                                        true && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs border-green-500 text-green-700"
                                        >
                                          Retroactive
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                {filters.visibleColumns.includes(
                                  'productGroups',
                                ) && (
                                  <TableCell className="w-[250px]">
                                    <div className="flex flex-wrap gap-1">
                                      {rule.qualifyingProductGroups.length ===
                                      0 ? (
                                        <Badge variant="outline">
                                          All Products
                                        </Badge>
                                      ) : (
                                        <>
                                          {rule.qualifyingProductGroups
                                            .slice(0, 3)
                                            .map((pg) => (
                                              <Badge
                                                key={pg.id}
                                                variant="outline"
                                                className="border-gray-200 text-gray-700"
                                              >
                                                {pg.name}
                                              </Badge>
                                            ))}
                                          {rule.qualifyingProductGroups.length >
                                            3 && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Badge
                                                  variant="outline"
                                                  className="border-gray-200 text-gray-700 cursor-help"
                                                >
                                                  +
                                                  {rule.qualifyingProductGroups
                                                    .length - 3}
                                                </Badge>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <div className="flex flex-col gap-1">
                                                  <p className="font-medium mb-1">
                                                    Additional Product Groups:
                                                  </p>
                                                  {rule.qualifyingProductGroups
                                                    .slice(3)
                                                    .map((pg) => (
                                                      <p
                                                        key={pg.id}
                                                        className="text-sm"
                                                      >
                                                        {pg.name}
                                                      </p>
                                                    ))}
                                                </div>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'currentTier',
                                ) && (
                                  <TableCell className="w-[150px]">
                                    <div className="flex items-center gap-2">
                                      {rule.currentTier === null ? (
                                        <Badge variant="secondary">None</Badge>
                                      ) : (
                                        <>
                                          <Badge
                                            variant="default"
                                            className="whitespace-nowrap"
                                          >
                                            Tier {rule.currentTier}
                                          </Badge>
                                          {rule.currentTierRebatePercentage !==
                                            null && (
                                            <span className="text-sm text-foreground">
                                              {rule.currentTierRebatePercentage.toFixed(
                                                2,
                                              )}
                                              %
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'nextTier',
                                ) && (
                                  <TableCell className="w-[150px]">
                                    <div className="flex items-center gap-2">
                                      {rule.nextTier !== null && (
                                        <>
                                          <Badge
                                            variant="outline"
                                            className="whitespace-nowrap"
                                          >
                                            Tier {rule.nextTier}
                                          </Badge>
                                          {rule.nextTierRebatePercentage !==
                                            null && (
                                            <span className="text-sm text-foreground">
                                              {rule.nextTierRebatePercentage.toFixed(
                                                2,
                                              )}
                                              %
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'currentVolume',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {formatCurrency(rule.currentVolume)}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'nextThreshold',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {formatCurrency(rule.nextTierThreshold)}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'volumeNeeded',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        {rule.error}
                                      </span>
                                    ) : rule.volumeNeeded === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span className="font-medium text-primary">
                                        {formatCurrency(rule.volumeNeeded)}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'currentRebate',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        -
                                      </span>
                                    ) : rule.currentRebateAmount === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span className="font-medium">
                                        {formatCurrency(
                                          rule.currentRebateAmount,
                                        )}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'nextTierRebate',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        -
                                      </span>
                                    ) : rule.nextTierRebateAmount === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span className="font-medium text-primary">
                                        {formatCurrency(
                                          rule.nextTierRebateAmount,
                                        )}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'rebateDelta',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        -
                                      </span>
                                    ) : rule.rebateDelta === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span
                                        className={`font-medium ${
                                          rule.rebateDelta > 0
                                            ? 'text-green-600'
                                            : 'text-muted-foreground'
                                        }`}
                                      >
                                        {rule.rebateDelta > 0 ? '+' : ''}
                                        {formatCurrency(rule.rebateDelta)}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'netBenefit',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        -
                                      </span>
                                    ) : rule.rebateDelta === null ||
                                      rule.volumeNeeded === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span
                                        className={`font-medium ${
                                          rule.rebateDelta - rule.volumeNeeded >
                                          0
                                            ? 'text-green-600'
                                            : 'text-muted-foreground'
                                        }`}
                                      >
                                        {rule.rebateDelta - rule.volumeNeeded >
                                        0
                                          ? '+'
                                          : ''}
                                        {formatCurrency(
                                          rule.rebateDelta - rule.volumeNeeded,
                                        )}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'efficiencyRatio',
                                ) && (
                                  <TableCell className="w-[140px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm italic">
                                        -
                                      </span>
                                    ) : rule.rebateDelta === null ||
                                      rule.volumeNeeded === null ? (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    ) : (
                                      <span className="font-medium text-primary">
                                        {(
                                          rule.rebateDelta / rule.volumeNeeded
                                        ).toFixed(2)}
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                {filters.visibleColumns.includes(
                                  'progress',
                                ) && (
                                  <TableCell className="w-[250px]">
                                    {rule.error ? (
                                      <span className="text-destructive text-sm">
                                        -
                                      </span>
                                    ) : rule.volumeNeeded === null ? (
                                      <span className="text-muted-foreground text-sm">
                                        At highest tier
                                      </span>
                                    ) : (
                                      <div className="w-full">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                          <span>
                                            {Math.round(
                                              rule.percentageComplete,
                                            )}
                                            %
                                          </span>
                                          <span className="text-muted-foreground">
                                            {formatCurrency(rule.volumeNeeded)}{' '}
                                            to go
                                          </span>
                                        </div>
                                        <Progress
                                          value={rule.percentageComplete}
                                        />
                                      </div>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TooltipProvider>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
